// The Phaser side of fog of war (docs/31). One instance per scene, drawn in its own board-camera
// layer (`fog`) that sits over the terrain, the light map, the gameplay marks, contact shadows and
// low mist, and under the units — the server strips the units the party can't see, so everything
// left on the board is in sight and stays crisp — and under the overlay camera (intent glyphs,
// the move overlay, route chips, target rings), which is never fogged: the player can aim into fog.
//
// Inside the fog layer (bottom → top):
//   shader      the fog itself (fogShader.ts), rendered to a texture at art resolution ≤ 20 Hz
//   ghosts      foes the party lost sight of: a cold translucent ghost of the sprite where it was
//               last seen, with age pips; foes only heard / smelled: a soft ripple and sound arcs
//   particles   wisps drifting along the frontier, ember motes at the edge of light underground,
//               big mist curls in weather
//   focus       a faint wash in the focused hero's player colour over its own sight
// No line is drawn at the edge of sight: the fog's own contrast (clear / memory / dark) is the edge.
// Units entering sight rise out of the fog (fade + lift + a puff); units leaving fade into it.
// When the board changes the masks blend old → new over FADE_MS, so the fog rolls back rather than
// snapping. Nothing animates under reduce motion; Soft / Off lighting drop the particles (the fog
// itself is gameplay and always draws).
import Phaser from 'phaser';
import type { FogView, TileData } from '../../core/sim/types';
import type { AtlasManifest } from '../../config';
import { unitFrame } from '../assets';
import { flipToFace } from '../hugeArt';
import { FOG_FRAG } from './fogShader';
import { ViewRender } from '../board/viewRender';
import { DataTexture } from '../board/dataTexture';
import { SUB, blendMasks, blindLook, fogMasks, frontier, markAge, veilLook, type FogLook, type FogMasks, type RGB } from './fog';
import { BACKDROP } from '../board/VoidLayer';

export interface FogOptions {
  /** Wisps, embers and curls (Full lighting only). */
  particles: boolean;
  /** Reduce motion: the fog stands still and nothing drifts. */
  still: boolean;
  /** The weather's wind (-1..1), so the fog drifts with the rain and the leaves. */
  wind: number;
  /** 1 full; Soft thins the memory veil a touch. */
  strength: number;
  /** The weather's kind, which colours a weather veil (sand, snow, rain, mist). */
  weather?: string;
}

export interface FogInfo { veil: string; unexplored: number; explored: number; inSight: number; lit: number; eyes: number; marks: number; frontier: number; shader: boolean; particles: number; focus?: number }

/** One party unit's eyes for the focus wash: its unit id and its player colour. */
export interface FogEyeColour { unit: number; colour: number; tile?: { x: number; y: number } }

const FADE_MS = 650;
const ENTER_MS = 620;
const LEAVE_MS = 520;
const RENDER_GAP_MS = 50;

const hex = (c: RGB): number => (Math.round(Math.min(1, c[0]) * 255) << 16) | (Math.round(Math.min(1, c[1]) * 255) << 8) | Math.round(Math.min(1, c[2]) * 255);
const mixc = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const ease = (k: number): number => 1 - Math.pow(1 - Math.max(0, Math.min(1, k)), 3);

let serial = 0;

/** The fog's look; a blind fog's dark is the scene's backdrop (docs/118 PT12). */
const lookOf = (fog: FogView, weather: string | undefined): FogLook => (fog.blind ? blindLook(veilLook(fog.veils, weather), BACKDROP) : veilLook(fog.veils, weather));

interface Ghost { obj: Phaser.GameObjects.Container; kind: 'seen' | 'heard' | 'smelled'; alpha: number; phase: number; g?: Phaser.GameObjects.Graphics; img?: Phaser.GameObjects.Image; wash?: Phaser.GameObjects.Image }
interface Fade { obj: Phaser.GameObjects.Container; start: number; from: number; to: number; y: number; lift: number; ms: number; destroy: boolean; lastY: number }

function ensureTextures(scene: Phaser.Scene): void {
  const tm = scene.textures;
  if (!tm.exists('fk-fog-wisp')) {
    // a soft elongated wisp: radial falloff squashed 2:1, a little lumpy so a crowd doesn't read as discs
    const w = 48, h = 24, c = tm.createCanvas('fk-fog-wisp', w, h)!; const x = c.getContext();
    for (const [cx, cy, r, a] of [[24, 12, 12, 0.55], [16, 13, 9, 0.4], [33, 11, 10, 0.45]] as const) {
      const g = x.createRadialGradient(cx, cy, 0, cx, cy, r * 1.8);
      g.addColorStop(0, `rgba(255,255,255,${a})`); g.addColorStop(0.55, `rgba(255,255,255,${a * 0.4})`); g.addColorStop(1, 'rgba(255,255,255,0)');
      x.save(); x.translate(0, cy); x.scale(1, 0.6); x.translate(0, -cy); x.fillStyle = g; x.fillRect(0, 0, w, h * 1.7); x.restore();
    }
    c.refresh(); c.setFilter(Phaser.Textures.FilterMode.LINEAR);
  }
  if (!tm.exists('fk-fog-dot')) { const c = tm.createCanvas('fk-fog-dot', 1, 1)!; const x = c.getContext(); x.fillStyle = '#fff'; x.fillRect(0, 0, 1, 1); c.refresh(); }
  if (!tm.exists('fk-fog-soft')) {
    const n = 32, c = tm.createCanvas('fk-fog-soft', n, n)!; const x = c.getContext();
    const g = x.createRadialGradient(n / 2, n / 2, 0, n / 2, n / 2, n / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.5, 'rgba(255,255,255,0.45)'); g.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = g; x.fillRect(0, 0, n, n); c.refresh(); c.setFilter(Phaser.Textures.FilterMode.LINEAR);
  }
}

export class FogLayer {
  private fog?: FogView;
  private look?: FogLook;
  private opts: FogOptions = { particles: true, still: false, wind: 0.25, strength: 1 };
  private colours: FogEyeColour[] = [];
  private focus?: number;
  // shader + masks
  private shader?: Phaser.GameObjects.Shader;
  /** The shader's pass, rendered only where the camera looks. */
  private view?: ViewRender;
  private image?: Phaser.GameObjects.Image;
  private keys?: { a: string; b: string; out: string };
  private canvases?: { a: DataTexture; b: DataTexture };
  private masks?: { from: FogMasks; to: FogMasks; cur: FogMasks; start: number };
  private dims = '';
  private lastRender = -1;
  private dirty = true;
  private time = 0;
  private frozenAt = 0;
  // canvas-renderer fallback
  private flat?: Phaser.GameObjects.Graphics;
  // drawn above the shader
  private ghostLayer: Phaser.GameObjects.Container;
  private particleLayer: Phaser.GameObjects.Container;
  private wash: Phaser.GameObjects.Graphics;
  private ghosts: Ghost[] = [];
  private emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  private puff?: Phaser.GameObjects.Particles.ParticleEmitter;
  private particleKey = '';
  private fades: Fade[] = [];
  private frontierCount = 0;

  constructor(private readonly scene: Phaser.Scene, private readonly layer: Phaser.GameObjects.Layer, private readonly cell: number, private readonly manifest: AtlasManifest) {
    ensureTextures(scene);
    this.ghostLayer = scene.add.container(0, 0);
    this.particleLayer = scene.add.container(0, 0);
    this.wash = scene.add.graphics();
    layer.add([this.ghostLayer, this.particleLayer, this.wash]);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
  }

  get active(): boolean { return !!this.fog; }
  /** docs/186 EA3: the memory veil the shader lays over explored squares out of sight (the standing art matches it). */
  get veil(): { memory: RGB; memWash: number; memDark: number; strength: number } | undefined {
    const l = this.look;
    return this.fog && l ? { memory: l.memory, memWash: l.memWash, memDark: l.memDark, strength: this.opts.strength } : undefined;
  }
  /** docs/35: whether the party sees a square now (always, on a clear field). */
  sees(x: number, y: number): boolean { const f = this.fog; return !f || f.cells[y * f.w + x] === 2; }
  /** docs/189: what the party knows of a square: 0 never seen, 1 seen before (the memory veil), 2 in sight now (always, on a clear field). */
  cellAt(x: number, y: number): 0 | 1 | 2 { const f = this.fog; if (!f) return 2; const v = f.cells[y * f.w + x]; return v === 2 ? 2 : v === 1 ? 1 : 0; }
  /** docs/189: the memory veil as a multiply, for things drawn over the fog (as EncounterTerrain darkens its standing art). */
  memoryTint(): RGB | undefined {
    const v = this.veil;
    if (!v) return undefined;
    const wash = v.memWash * v.strength, dark = v.memDark * v.strength;
    return [0, 1, 2].map((c) => Math.min(1, (1 - wash + (v.memory[c]! * wash) / 0.5) * (1 - dark))) as unknown as RGB;
  }

  setMode(opts: Partial<FogOptions>): void {
    const before = this.opts;
    this.opts = { ...this.opts, ...opts };
    if (this.fog && before.weather !== this.opts.weather) this.look = lookOf(this.fog, this.opts.weather);
    if (before.particles !== this.opts.particles || before.still !== this.opts.still || before.wind !== this.opts.wind || before.weather !== this.opts.weather) { this.particleKey = ''; this.syncParticles(); }
    this.dirty = true;
  }

  /**
   * The fog for a board (undefined: a clear field — everything goes). Colours give each party
   * unit's player colour for the focus wash. Cheap to call on every snapshot: the shader is kept, the
   * masks blend from the old board's to the new one's.
   */
  set(board: { w: number; h: number; tiles: ReadonlyArray<TileData | undefined> }, fog: FogView | undefined, colours: FogEyeColour[]): void {
    if (!fog || fog.w !== board.w || fog.h !== board.h || fog.cells.length !== board.w * board.h) { this.clear(); return; }
    this.fog = fog; this.colours = colours;
    this.look = lookOf(fog, this.opts.weather);
    const dims = `${fog.w}x${fog.h}`;
    if (dims !== this.dims) this.buildShader(board);
    this.updateMasks(true);
    this.drawGhosts();
    this.drawFocus();
    this.syncParticles();
    this.dirty = true;
  }

  /** The hero to show the sight of (selected or hovered unit id), or none. */
  setFocus(unit: number | undefined): void {
    const u = unit !== undefined && this.fog?.eyes.some((e) => e.unit === unit) ? unit : undefined;
    if (u === this.focus) return;
    this.focus = u;
    if (!this.fog) return;
    this.updateMasks(false);
    this.drawFocus();
    this.dirty = true;
  }

  /** A unit that just came into the party's sight: rise it out of the fog. */
  enter(obj: Phaser.GameObjects.Container): void {
    if (!this.fog) return;
    const to = obj.alpha;
    if (this.opts.still) return;
    obj.setAlpha(0);
    this.fades.push({ obj, start: this.time, from: 0, to, y: obj.y, lift: 5, ms: ENTER_MS, destroy: false, lastY: obj.y + 5 });
    obj.y += 5;
    this.puffAt(obj.x, obj.y - this.cell * 0.5, 5);
  }

  /** A unit that just left the party's sight: fade it into the fog, then destroy it. */
  leave(obj: Phaser.GameObjects.Container): void {
    if (this.opts.still || !this.fog || obj.alpha <= 0.01) { obj.destroy(); return; }
    this.fades.push({ obj, start: this.time, from: obj.alpha, to: 0, y: obj.y, lift: 3, ms: LEAVE_MS, destroy: true, lastY: obj.y });
    this.puffAt(obj.x, obj.y - this.cell * 0.5, 4);
  }

  info(): FogInfo | undefined {
    const f = this.fog;
    if (!f || !this.look) return undefined;
    const n = (v: number) => f.cells.filter((c) => c === v).length;
    return { veil: this.look.key, unexplored: n(0), explored: n(1), inSight: n(2), lit: f.lit.filter((l) => l === 1).length, eyes: f.eyes.length, marks: f.marks.length, frontier: this.frontierCount, shader: !!this.shader, particles: this.emitters.length, ...(this.focus !== undefined ? { focus: this.focus } : {}) };
  }

  // ---- shader and masks ------------------------------------------------------------------------

  private buildShader(board: { w: number; h: number }): void {
    this.dropShader();
    const { scene, cell } = this;
    this.dims = `${board.w}x${board.h}`;
    const W = board.w * cell, H = board.h * cell;
    this.frozenAt = (board.w * 7 + board.h * 13) % 50;
    if (scene.game.renderer.type !== Phaser.WEBGL) {
      this.flat = scene.add.graphics();
      this.layer.addAt(this.flat, 0);
      return;
    }
    const id = ++serial;
    this.keys = { a: `fk-fog-a-${id}`, b: `fk-fog-b-${id}`, out: `fk-fog-out-${id}` };
    const gw = board.w * SUB, gh = board.h * SUB;
    // the masks are fed straight from their bytes, stretched smoothly (docs/147 PG1)
    const a = new DataTexture(scene, this.keys.a, gw, gh, new Uint8ClampedArray(gw * gh * 4), true);
    const b = new DataTexture(scene, this.keys.b, gw, gh, new Uint8ClampedArray(gw * gh * 4), true);
    this.canvases = { a, b };
    const s = new Phaser.GameObjects.Shader(scene, {
      name: 'fkFog',
      fragmentSource: FOG_FRAG,
      setupUniforms: (set: (name: string, value: unknown) => void) => {
        const l = this.look!;
        const still = this.opts.still;
        const wind = this.opts.wind;
        set('uA', 0); set('uB', 1);
        set('uSize', [W, H]); set('uCells', [board.w, board.h]);
        set('uTime', still ? this.frozenAt : this.time / 1000);
        set('uDrift', [(0.03 + 0.08 * wind) * l.speed, 0.012 * l.speed]);
        set('uStretch', l.stretch);
        set('uDeep', [...l.deep]); set('uWisp', [...l.wisp]); set('uMem', [...l.memory]); set('uRim', [...l.rim]); set('uWarm', [...l.warm]);
        set('uDense', l.dense); set('uMemWash', l.memWash); set('uMemDark', l.memDark);
        set('uFocus', this.focus !== undefined ? 1 : 0);
        set('uStrength', this.opts.strength);
      },
    }, 0, 0, W, H, [this.keys.a, this.keys.b]);
    s.setRenderToTexture(this.keys.out);
    this.image = scene.add.image(0, 0, this.keys.out).setOrigin(0, 0).setDisplaySize(W, H);
    this.layer.addAt(this.image, 0);
    this.shader = s;
    this.view = new ViewRender(s, scene.cameras.main, W, H);
    this.masks = undefined;
  }

  private updateMasks(blend: boolean): void {
    const fog = this.fog!;
    const focus = this.focus !== undefined ? new Set(fog.eyes.find((e) => e.unit === this.focus)?.tiles ?? []) : undefined;
    const to = fogMasks(fog, focus);
    if (this.flat) { this.drawFlat(); return; }
    if (!this.canvases) return;
    const prev = this.masks;
    const fresh = !prev || prev.cur.gw !== to.gw || prev.cur.gh !== to.gh;
    if (fresh || !blend || this.opts.still) {
      // focus changes swap the B mask at once (it's a highlight), but keep any A blend running
      if (!fresh && !blend && prev) { prev.to = { ...prev.to, b: to.b }; prev.cur.b.set(to.b); }
      else this.masks = { from: to, to, cur: { gw: to.gw, gh: to.gh, a: new Uint8ClampedArray(to.a), b: new Uint8ClampedArray(to.b) }, start: -Infinity };
    } else {
      this.masks = { from: { gw: to.gw, gh: to.gh, a: new Uint8ClampedArray(prev.cur.a), b: new Uint8ClampedArray(prev.cur.b) }, to, cur: prev.cur, start: this.time };
    }
    this.upload();
  }

  private upload(): void {
    const m = this.masks, c = this.canvases;
    if (!m || !c) return;
    c.a.upload(m.cur.a); c.b.upload(m.cur.b);
    this.dirty = true;
  }

  /** Canvas renderer: flat per-square veils, no shader. */
  private drawFlat(): void {
    const g = this.flat, fog = this.fog, l = this.look;
    if (!g || !fog || !l) return;
    g.clear();
    const c = this.cell;
    for (let i = 0; i < fog.cells.length; i++) {
      const v = fog.cells[i];
      if (v === 2) continue;
      g.fillStyle(hex(v === 0 ? l.deep : l.memory), v === 0 ? l.dense : 0.7);
      g.fillRect((i % fog.w) * c, Math.floor(i / fog.w) * c, c, c);
    }
  }

  // ---- marks -----------------------------------------------------------------------------------

  private drawGhosts(): void {
    for (const g of this.ghosts) g.obj.destroy();
    this.ghosts = [];
    const fog = this.fog!, cell = this.cell, l = this.look!;
    const has = (k: string, f: string) => this.scene.textures.exists(k) && this.scene.textures.get(k).has(f);
    fog.marks.forEach((m, i) => {
      const age = markAge(m, fog.pulse);
      const x = m.tile.x * cell + cell / 2, y = (m.tile.y + 1) * cell;
      const obj = this.scene.add.container(x, y);
      const ghost: Ghost = { obj, kind: m.sense, alpha: age.alpha, phase: i * 1.37 };
      if (m.sense === 'seen') {
        const { key, frame } = unitFrame(this.manifest, { sprite: 'goblin', appearance: m.look }, has);
        // a cold, hollow echo of the body: pale-tinted, translucent, its base marker dashed
        const base = this.scene.add.graphics();
        const r = Math.floor(cell / 2) - 3;
        base.lineStyle(1, 0xff7b6b, 0.75);
        for (let k = 0; k < 8; k += 2) {
          const pts = [[0, -3], [r, -1], [0, 1], [-r, -1], [0, -3]] as const;
          const seg = Math.floor(k / 2), a = pts[seg]!, b = pts[seg + 1]!;
          base.lineBetween(a[0], a[1], a[0] + (b[0] - a[0]) * 0.55, a[1] + (b[1] - a[1]) * 0.55);
        }
        // the body in cold monochrome: a blue-grey multiply, then a pale fill washed over it, facing
        // west (docs/95: a commissioned body by which way its art faces)
        const west = flipToFace(key, -1);
        const img = this.scene.add.image(0, 0, key, frame).setOrigin(0.5, 1).setFlipX(west).setTint(hex([0.5, 0.58, 0.8])).setTintMode(Phaser.TintModes.MULTIPLY);
        const wash = this.scene.add.image(0, 0, key, frame).setOrigin(0.5, 1).setFlipX(west).setTint(hex(mixc(l.rim, [0.8, 0.88, 1], 0.6))).setTintMode(Phaser.TintModes.FILL).setAlpha(0.42);
        const pips = this.scene.add.graphics();
        for (let p = 0; p < age.pips; p++) { pips.fillStyle(0xe8e2d0, 0.85 - p * 0.15); pips.fillRect(-((age.pips - 1) * 3) / 2 + p * 3 - 1, 2, 2, 1); }
        obj.add([base, img, wash, pips]);
        ghost.wash = wash;
        ghost.img = img;
      } else {
        const g = this.scene.add.graphics();
        obj.add(g);
        ghost.g = g;
        obj.y = m.tile.y * cell + cell / 2;
      }
      this.ghostLayer.add(obj);
      this.ghosts.push(ghost);
    });
  }

  private animateGhosts(time: number): void {
    const t = this.opts.still ? 0 : time / 1000;
    const cell = this.cell;
    for (const g of this.ghosts) {
      if (g.kind === 'seen') {
        // breathing, and a one-pixel bob
        const b = 0.82 + 0.18 * Math.sin(t * 1.6 + g.phase);
        g.obj.setAlpha(g.alpha * b);
        const bob = Math.round(Math.sin(t * 1.1 + g.phase) * 0.6);
        if (g.img) g.img.y = bob;
        if (g.wash) g.wash.y = bob;
        continue;
      }
      // heard: three rings expanding from the spot, and sound arcs either side; smelled: slower, greener, wavering
      const gr = g.g!;
      gr.clear();
      const heard = g.kind === 'heard';
      const colour = heard ? 0xf4d9a0 : 0xb8d98a;
      const period = heard ? 1.8 : 2.6;
      for (let k = 0; k < 3; k++) {
        const ph = ((t / period + k / 3 + g.phase) % 1 + 1) % 1;
        const r = 2 + ph * cell * 0.62;
        gr.lineStyle(1, colour, g.alpha * 0.9 * (1 - ph));
        if (heard) gr.strokeCircle(0, 0, r);
        else gr.strokeEllipse(Math.sin(t * 2 + k) * 1.2, 0, r * 2, r * 1.3);
      }
      const pulse = 0.6 + 0.4 * Math.sin(t * 3 + g.phase);
      gr.lineStyle(1, colour, g.alpha * pulse);
      for (const side of [-1, 1]) for (let a = 0; a < 2; a++) {
        const rr = 4 + a * 3;
        gr.beginPath(); gr.arc(0, 0, rr, side > 0 ? -0.6 : Math.PI - 0.6, side > 0 ? 0.6 : Math.PI + 0.6); gr.strokePath();
      }
      gr.fillStyle(colour, g.alpha); gr.fillRect(-0.5, -0.5, 1, 1);
    }
  }

  // ---- focus -----------------------------------------------------------------------------------

  /** The focused hero: a faint wash of its player colour over its own sight (no outline). */
  private drawFocus(): void {
    const g = this.wash, fog = this.fog!;
    g.clear();
    const eye = this.focus !== undefined ? fog.eyes.find((e) => e.unit === this.focus) : undefined;
    if (!eye) return;
    const cell = this.cell, w = fog.w;
    g.fillStyle(this.colours.find((c) => c.unit === eye.unit)?.colour ?? 0xe8e2d0, 0.08);
    for (const i of eye.tiles) g.fillRect((i % w) * cell, Math.floor(i / w) * cell, cell, cell);
  }

  // ---- particles ---------------------------------------------------------------------------------

  private syncParticles(): void {
    const fog = this.fog, look = this.look;
    const front = fog ? frontier(fog) : [];
    this.frontierCount = front.length;
    const key = fog && look && this.opts.particles && !this.opts.still ? `${look.key}|${look.wisp.join()}|${front.join(',')}|${fog.lit.join('')}|${this.opts.wind}` : '';
    if (key === this.particleKey) return;
    this.particleKey = key;
    for (const e of this.emitters) e.destroy();
    this.emitters = []; this.puff = undefined;
    if (!key || !fog || !look) return;
    const { scene, cell } = this;
    const W = fog.w * cell, H = fog.h * cell;
    const wind = this.opts.wind, dir = wind < 0 ? -1 : 1, gust = Math.abs(wind);
    const inside = (p: Phaser.GameObjects.Particles.Particle, r: number): number => Math.max(0, Math.min(1, Math.min(p.x, W - p.x, p.y, H - p.y) / r));
    const cellsZone = (cells: number[], pad = 0): Phaser.Types.GameObjects.Particles.ParticleEmitterRandomZoneConfig => ({
      type: 'random',
      source: { getRandomPoint: (p: Phaser.Types.Math.Vector2Like) => { const i = cells[Math.floor(Math.random() * cells.length)]!; p.x = ((i % fog.w) - pad + Math.random() * (1 + 2 * pad)) * cell; p.y = (Math.floor(i / fog.w) - pad + Math.random() * (1 + 2 * pad)) * cell; } },
    });
    const emit = (tex: string, cfg: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig) => {
      const e = scene.add.particles(0, 0, tex, cfg);
      this.particleLayer.add(e); this.emitters.push(e);
      return e;
    };
    // the fog side of a square's frontier, so wisps drift where the dark begins
    const lightish = (look.deep[0] + look.deep[1] + look.deep[2]) / 3 > 0.3;
    if (look.wisps && front.length) {
      const cap = Math.min(42, 6 + front.length * 0.9), life = 6500;
      emit('fk-fog-wisp', {
        emitZone: cellsZone(front, 0.15), frequency: life / cap, lifespan: { min: life * 0.7, max: life * 1.2 },
        speedX: { min: dir * (1 + 4 * gust), max: dir * (3 + 9 * gust) }, speedY: { min: -1.5, max: 1.5 },
        scaleX: { min: 0.7, max: 1.25 }, scaleY: { min: 0.55, max: 0.9 }, rotate: { min: -12, max: 12 },
        tint: hex(look.wisp),
        alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => (lightish ? 0.16 : 0.1) * Math.sin(t * Math.PI) * inside(p, cell) },
        advance: life,
      });
    }
    if (look.embers) {
      // motes swirling at the edge of the light: lit squares with a dark neighbour
      const edge: number[] = [];
      for (let i = 0; i < fog.lit.length; i++) {
        if (fog.lit[i] !== 1) continue;
        const x = i % fog.w, y = Math.floor(i / fog.w);
        if ([[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => { const nx = x + dx!, ny = y + dy!; return nx >= 0 && ny >= 0 && nx < fog.w && ny < fog.h && fog.lit[ny * fog.w + nx] !== 1; })) edge.push(i);
      }
      if (edge.length) {
        const cap = Math.min(28, 4 + edge.length * 0.8), life = 2600;
        emit('fk-fog-dot', {
          emitZone: cellsZone(edge), frequency: life / cap, lifespan: { min: life * 0.6, max: life * 1.3 },
          speedX: { min: -4, max: 4 }, speedY: { min: -9, max: -3 }, tint: [0xffc47a, 0xff9a4a, 0xffe2a8], blendMode: Phaser.BlendModes.ADD,
          x: { onEmit: () => 0, onUpdate: (p, _k, t, v) => v + Math.sin(t * 12 + p.life * 0.01) * 0.2 },
          alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => 0.8 * Math.sin(t * Math.PI) * (0.6 + 0.4 * Math.sin(t * 31 + p.life)) * inside(p, cell / 2) },
          advance: life,
        });
      }
    }
    if (look.curls) {
      // weather: big slow mist curls rolling over everything out of sight
      const unseen: number[] = [];
      for (let i = 0; i < fog.cells.length; i++) if (fog.cells[i] !== 2) unseen.push(i);
      if (unseen.length) {
        const cap = Math.min(16, 3 + unseen.length / 10), life = 11000;
        emit('fk-fog-wisp', {
          emitZone: cellsZone(unseen), frequency: life / cap, lifespan: { min: life * 0.8, max: life * 1.2 },
          speedX: { min: dir * (3 + 10 * gust), max: dir * (6 + 18 * gust) }, speedY: { min: -2, max: 2 }, scaleX: { min: 2.2, max: 3.4 }, scaleY: { min: 1.4, max: 2.2 },
          rotate: { min: -8, max: 8 }, tint: [hex(look.wisp), hex(look.rim)],
          alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => 0.13 * Math.sin(t * Math.PI) * inside(p, cell * 1.5) },
          advance: life,
        });
      }
    }
    this.puff = emit('fk-fog-wisp', {
      emitting: false, lifespan: { min: 700, max: 1100 }, speedX: { min: -14, max: 14 }, speedY: { min: -10, max: 2 }, scale: { start: 0.35, end: 0.9 },
      tint: [hex(look.wisp), hex(look.rim)], alpha: { onEmit: () => 0, onUpdate: (_p, _k, t) => 0.4 * Math.sin(t * Math.PI) * (1 - t * 0.5) },
    });
  }

  private puffAt(x: number, y: number, n: number): void {
    if (this.puff && !this.opts.still) this.puff.emitParticleAt(x, y, n);
  }

  // ---- per frame ---------------------------------------------------------------------------------

  update(time: number): void {
    this.time = time;
    this.runFades(time);
    if (!this.fog) return;
    this.animateGhosts(time);
    const m = this.masks;
    const due = this.lastRender < 0 || time - this.lastRender >= RENDER_GAP_MS;
    if (!this.shader || !this.view) return;
    // the camera looking past what was last rendered renders now, whatever the tick
    const off = this.view.needed;
    if (!due && !off) return;
    if (due && m && m.start > -Infinity) {
      // the fog rolls back (or in) from the last board's masks to this one's
      const k = (time - m.start) / FADE_MS;
      blendMasks(m.from.a, m.to.a, ease(k), m.cur.a);
      if (k >= 1) m.start = -Infinity;
      this.upload();
    }
    if (this.opts.still && !this.dirty && !off) return;
    this.lastRender = time;
    this.dirty = false;
    this.view.render();
  }

  private runFades(time: number): void {
    if (!this.fades.length) return;
    this.fades = this.fades.filter((f) => {
      if (!f.obj.active) return false;
      const k = (time - f.start) / f.ms;
      const e = ease(k);
      f.obj.setAlpha(f.from + (f.to - f.from) * e);
      // only steer the body while nothing else (playback) has moved it
      if (Math.abs(f.obj.y - f.lastY) < 0.01) { f.obj.y = f.y + f.lift * (f.destroy ? e : 1 - e); f.lastY = f.obj.y; }
      if (k < 1) return true;
      if (f.destroy) f.obj.destroy();
      else if (Math.abs(f.obj.y - f.lastY) < 0.01) { f.obj.y = f.y; f.obj.setAlpha(f.to); }
      return false;
    });
  }

  // ---- teardown ----------------------------------------------------------------------------------

  private dropShader(): void {
    this.image?.destroy(); this.image = undefined;
    this.shader?.destroy(); this.shader = undefined; this.view = undefined;
    this.flat?.destroy(); this.flat = undefined;
    if (this.keys) {
      this.canvases?.a.destroy(); this.canvases?.b.destroy();
      if (this.scene.textures.exists(this.keys.out)) this.scene.textures.remove(this.keys.out);
    }
    this.keys = undefined; this.canvases = undefined; this.masks = undefined; this.dims = '';
  }

  clear(): void {
    this.dropShader();
    for (const g of this.ghosts) g.obj.destroy();
    for (const e of this.emitters) e.destroy();
    this.ghosts = []; this.emitters = []; this.puff = undefined; this.particleKey = '';
    this.wash.clear();
    this.fog = undefined; this.look = undefined; this.focus = undefined;
    for (const f of this.fades) { if (f.destroy) f.obj.destroy(); else if (f.obj.active) { f.obj.setAlpha(f.to); f.obj.y = f.y; } }
    this.fades = [];
  }

  destroy(): void {
    this.clear();
    this.ghostLayer.destroy(); this.particleLayer.destroy(); this.wash.destroy();
  }
}
